import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  RefreshCw,
  AlertTriangle,
  Clock,
  XCircle,
  Unlock,
  Search,
} from "lucide-react";

const STATUS_TONE = {
  pending: { color: "bg-amber-50 text-amber-700 border-amber-200", title: "Stuck Pending", icon: Clock, hint: "Created but never reserved" },
  approved: { color: "bg-blue-50 text-blue-700 border-blue-200", title: "Stuck Approved", icon: Clock, hint: "Reserved but never dispatched — reservations may be auto-released" },
  dispatched: { color: "bg-indigo-50 text-indigo-700 border-indigo-200", title: "Stuck Dispatched", icon: AlertTriangle, hint: "Likely lost in transit" },
  partial: { color: "bg-purple-50 text-purple-700 border-purple-200", title: "Stuck Partial", icon: AlertTriangle, hint: "Partial fulfilment never closed" },
};

export default function ReconciliationView({ shops = [] }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [shopFilter, setShopFilter] = useState("all");
  // Thresholds (in days)
  const [thresholds, setThresholds] = useState({
    pending: 7,
    approved: 7,
    dispatched: 3,
    partial: 5,
  });
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        pending_days: thresholds.pending,
        approved_days: thresholds.approved,
        dispatched_days: thresholds.dispatched,
        partial_days: thresholds.partial,
      };
      if (shopFilter !== "all") params.shop_id = shopFilter;
      const { data } = await api.get("/management/reconciliation", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [thresholds, shopFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const releaseStale = async (dryRun) => {
    if (
      !dryRun &&
      !window.confirm(
        `Release ALL active reservations on items stuck in 'approved' for ≥ ${thresholds.approved} day(s)?\n\n` +
          `This frees the reserved stock and flips affected items back to 'pending'.`
      )
    )
      return;
    try {
      const { data: res } = await api.post(
        "/management/reconciliation/release-stale",
        {
          approved_days: thresholds.approved,
          shop_id: shopFilter !== "all" ? shopFilter : null,
          dry_run: dryRun,
        }
      );
      if (dryRun) {
        toast.success(
          `Dry run: would release ${res.released} reservation(s) across ${res.items_affected} item(s)`
        );
      } else {
        toast.success(
          `Released ${res.released} stale reservation(s) across ${res.items_affected} item(s)`
        );
        fetchData();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cancelItem = async (id, productName) => {
    if (!window.confirm(`Cancel restock request for "${productName}"?\n\nThis releases any reservations and marks the item as cancelled.`))
      return;
    try {
      await api.post(`/management/restock-queue/${id}/cancel`);
      toast.success("Cancelled");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filterRow = (it) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (it.product_snapshot?.product_name || "").toLowerCase().includes(s) ||
      (it.product_snapshot?.brand_name || "").toLowerCase().includes(s) ||
      (it.destination_shop_name || "").toLowerCase().includes(s)
    );
  };

  const counts = data?.counts || {
    stuck_pending: 0,
    stuck_approved: 0,
    stuck_dispatched: 0,
    stuck_partial: 0,
    total: 0,
  };

  return (
    <div className="space-y-4" data-testid="reconciliation-view">
      {/* Header / config */}
      <Card className="p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Reconciliation — Stuck Items
            </h3>
            <p className="text-xs text-muted-foreground">
              Items not moving forward in expected time. Tune thresholds below. {" "}
              <span className="text-red-600 font-semibold">
                {counts.total} item{counts.total === 1 ? "" : "s"} need attention.
              </span>
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <ThresholdInput
              label="Pending ≥"
              value={thresholds.pending}
              onChange={(v) => setThresholds((t) => ({ ...t, pending: v }))}
              testid="rc-th-pending"
            />
            <ThresholdInput
              label="Approved ≥"
              value={thresholds.approved}
              onChange={(v) => setThresholds((t) => ({ ...t, approved: v }))}
              testid="rc-th-approved"
            />
            <ThresholdInput
              label="Dispatched ≥"
              value={thresholds.dispatched}
              onChange={(v) => setThresholds((t) => ({ ...t, dispatched: v }))}
              testid="rc-th-dispatched"
            />
            <ThresholdInput
              label="Partial ≥"
              value={thresholds.partial}
              onChange={(v) => setThresholds((t) => ({ ...t, partial: v }))}
              testid="rc-th-partial"
            />
            <Select value={shopFilter} onValueChange={setShopFilter}>
              <SelectTrigger className="h-9 w-44" data-testid="rc-shop-filter">
                <SelectValue placeholder="All shops" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All shops</SelectItem>
                {shops.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              data-testid="rc-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="rc-kpis">
        <KpiCard label="Stuck Pending" value={counts.stuck_pending} tone="amber" />
        <KpiCard label="Stuck Approved" value={counts.stuck_approved} tone="blue" />
        <KpiCard label="Stuck Dispatched" value={counts.stuck_dispatched} tone="red" />
        <KpiCard label="Stuck Partial" value={counts.stuck_partial} tone="purple" />
      </div>

      {/* Master action */}
      {counts.stuck_approved > 0 && (
        <Card className="p-3 border-blue-200 bg-blue-50/40">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="flex-1 text-xs">
              <div className="font-semibold flex items-center gap-1">
                <Unlock className="w-3.5 h-3.5" /> Auto-release stale approved reservations
              </div>
              <div className="text-muted-foreground">
                Frees stock reserved &gt; {thresholds.approved} day(s) ago and flips
                those items back to <strong>pending</strong>. Dry-run first to preview.
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => releaseStale(true)}
              data-testid="rc-dry-run-btn"
            >
              Dry Run
            </Button>
            <Button
              size="sm"
              onClick={() => releaseStale(false)}
              className="text-white"
              style={{ backgroundColor: "#9E6D5B" }}
              data-testid="rc-release-stale-btn"
            >
              <Unlock className="w-3.5 h-3.5 mr-1" />
              Release All Stale
            </Button>
          </div>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          className="pl-8 h-9"
          placeholder="Search by product, brand, or shop..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="rc-search-input"
        />
      </div>

      {/* Buckets */}
      {(["pending", "approved", "dispatched", "partial"]).map((status) => {
        const tone = STATUS_TONE[status];
        const items = (data?.[`stuck_${status}`] || []).filter(filterRow);
        if (items.length === 0) return null;
        const Icon = tone.icon;
        return (
          <Card key={status} className={`p-0 border ${tone.color}`} data-testid={`rc-bucket-${status}`}>
            <div className="p-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <div>
                  <div className="font-semibold text-sm">{tone.title}</div>
                  <div className="text-[11px] text-muted-foreground">{tone.hint}</div>
                </div>
              </div>
              <Badge variant="outline" className="font-mono" data-testid={`rc-bucket-${status}-count`}>
                {items.length}
              </Badge>
            </div>
            <div className="divide-y bg-white">
              {items.map((it) => (
                <ReconRow
                  key={it.id}
                  item={it}
                  onCancel={() =>
                    cancelItem(it.id, it.product_snapshot?.product_name)
                  }
                />
              ))}
            </div>
          </Card>
        );
      })}

      {counts.total === 0 && !loading && (
        <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="rc-all-clear">
          <div className="text-emerald-600 font-medium mb-1">All clear!</div>
          <div>No restock items are stuck beyond the current thresholds.</div>
        </Card>
      )}
    </div>
  );
}

function ThresholdInput({ label, value, onChange, testid }) {
  return (
    <div className="flex flex-col">
      <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">
        {label}
      </Label>
      <div className="flex items-center gap-1">
        <Input
          type="number"
          min="1"
          value={value}
          onChange={(e) => onChange(Math.max(1, Number(e.target.value) || 1))}
          className="h-8 w-16 text-right font-mono"
          data-testid={testid}
        />
        <span className="text-[10px] text-muted-foreground">d</span>
      </div>
    </div>
  );
}

function KpiCard({ label, value, tone }) {
  const toneColor = {
    amber: "text-amber-700",
    blue: "text-blue-700",
    red: "text-red-700",
    purple: "text-purple-700",
  }[tone] || "text-slate-700";
  return (
    <Card className="p-3" data-testid={`rc-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${toneColor}`}>{value}</div>
    </Card>
  );
}

function ReconRow({ item, onCancel }) {
  const p = item.product_snapshot || {};
  return (
    <div className="p-3 flex items-center gap-3" data-testid={`rc-row-${item.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{p.product_name || "—"}</div>
        <div className="text-[11px] text-muted-foreground truncate">
          {p.brand_name || "—"} · {p.auto_barcode || ""}
        </div>
      </div>
      <Badge variant="outline">{item.destination_shop_name}</Badge>
      <div className="text-xs text-right">
        <div className="font-mono font-semibold">
          {item.quantity_requested} req · {item.quantity_fulfilled || 0} done
        </div>
        <div className="text-red-600 font-medium">
          stuck {item.days_stuck}d
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onCancel}
        className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
        data-testid={`rc-cancel-${item.id}`}
      >
        <XCircle className="w-3.5 h-3.5 mr-1" />
        Cancel
      </Button>
    </div>
  );
}
